import React, { useState, useRef } from 'react';
import { bool, func, node, number, string } from 'prop-types';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import classNames from 'classnames';

import { Button, Modal } from '../../components';
import {
  canvasPreview,
  getCenteredAspectCrop,
  getCroppedImg,
  useDebounceEffect,
} from './imageCrop';

import css from './ImageCropModal.css';
import 'react-image-crop/dist/ReactCrop.css';

function CropImage({
  src,
  imgRef,
  aspect,
  setAspect,
  aspectChangeable,
  crop,
  setCrop,
  setCompletedCrop,
  onImageLoad,
}) {
  function handleToggleAspectClick(aspect) {
    if (aspect === 16 / 9) {
      setAspect(16 / 9);
    } else if (aspect === 9 / 16) {
      setAspect(9 / 16);
    } else if (aspect === 1) {
      setAspect(1);
    } else if (aspect === 4 / 5) {
      setAspect(4 / 5);
    } else if (aspect === 3 / 4) {
      setAspect(3 / 4);
    }

    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const newCrop = getCenteredAspectCrop(width, height, aspect);
      setCrop(newCrop);
      setCompletedCrop(convertToPixelCrop(newCrop, width, height));
    }
  }
  return (
    <div>
      {aspectChangeable && (
        <div className={css.aspectButtons}>
          <div
            type="button"
            className={classNames(
              aspect === 16 / 9 ? css.selectedAspectButton : css.aspectButton,
              css.aspectButtonLandscape
            )}
            onClick={() => handleToggleAspectClick(16 / 9)}
          >
            16:9
          </div>
          <div
            type="button"
            className={classNames(
              aspect === 1 ? css.selectedAspectButton : css.aspectButton,
              css.aspectButtonSquare
            )}
            onClick={() => handleToggleAspectClick(1)}
          >
            1:1
          </div>
          <div
            type="button"
            className={classNames(
              aspect === 3 / 4 ? css.selectedAspectButton : css.aspectButton,
              css.aspectButton34
            )}
            onClick={() => handleToggleAspectClick(3 / 4)}
          >
            3:4
          </div>
          <div
            type="button"
            className={classNames(
              aspect === 4 / 5 ? css.selectedAspectButton : css.aspectButton,
              css.aspectButton45
            )}
            onClick={() => handleToggleAspectClick(4 / 5)}
          >
            4:5
          </div>
          <div
            type="button"
            className={classNames(
              aspect === 9 / 16 ? css.selectedAspectButton : css.aspectButton,
              css.aspectButtonPortrait
            )}
            onClick={() => handleToggleAspectClick(9 / 16)}
          >
            9:16
          </div>
        </div>
      )}
      <ReactCrop
        className={css.cropImage}
        crop={crop}
        aspect={aspect}
        onImageLoaded={onImageLoad}
        onChange={(_, percentCrop) => {
          setCrop(percentCrop);
        }}
        onComplete={c => setCompletedCrop(c)}
        keepSelection={true}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Crop preview"
          className={css.cropImagePreview}
          onLoad={onImageLoad}
        />
      </ReactCrop>
    </div>
  );
}

/**
 * Crops a picked image in a modal. Owns the crop state and the hidden preview
 * canvas; onConfirm receives the cropped image as a JPEG File named fileName,
 * and onError the error when the cropped image can't be encoded.
 */
const ImageCropModal = props => {
  const {
    id,
    isOpen,
    src,
    fileName,
    aspect,
    aspectChangeable,
    onAspectChange,
    instructions,
    confirmLabel,
    onConfirm,
    onError,
    onClose,
    onManageDisableScrolling,
  } = props;

  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState();
  const [confirming, setConfirming] = useState(false);
  const [cropSrc, setCropSrc] = useState(src);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // A newly picked image starts without a crop until it has loaded. Reset
  // during render so it always lands before the new image's onLoad.
  if (src !== cropSrc) {
    setCropSrc(src);
    setCrop(null);
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      const centeredCrop = getCenteredAspectCrop(width, height, aspect);
      setCrop(centeredCrop);
      setCompletedCrop(convertToPixelCrop(centeredCrop, width, height));
    }
  }

  const handleConfirm = () => {
    if (confirming) return;
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;

    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    if (typeof window !== 'undefined') {
      setConfirming(true);
      getCroppedImg(image, completedCrop, fileName, previewCanvas)
        .then(resultFile => {
          setConfirming(false);
          onConfirm(resultFile);
        })
        .catch(err => {
          // The parent says why (e.g. an image too big for the browser to
          // encode); the crop stays as it was, so the user can try again.
          setConfirming(false);
          if (onError) {
            onError(err);
          }
        });
    }
  };

  return (
    <>
      <Modal
        id={id}
        contentClassName={css.modalContent}
        isOpen={isOpen}
        onClose={onClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.cropImageWrapper}>
          {instructions ? <div className={css.instructions}>{instructions}</div> : null}
          <CropImage
            src={src}
            imgRef={imgRef}
            aspect={aspect}
            setAspect={onAspectChange}
            aspectChangeable={aspectChangeable}
            crop={crop}
            setCrop={setCrop}
            setCompletedCrop={setCompletedCrop}
            onImageLoad={onImageLoad}
          />
        </div>

        <Button
          className={css.submitImageButton}
          type="button"
          onClick={handleConfirm}
          inProgress={confirming}
          disabled={confirming}
          ready={false}
        >
          {confirmLabel}
        </Button>
      </Modal>
      <div>
        <canvas
          ref={previewCanvasRef}
          style={{
            position: 'absolute',
            top: '-200vh',
            visibility: 'hidden',
            objectFit: 'contain',
            width: completedCrop?.width,
            height: completedCrop?.height,
          }}
        />
      </div>
    </>
  );
};

ImageCropModal.defaultProps = {
  isOpen: false,
  src: null,
  aspectChangeable: false,
  instructions: null,
  confirmLabel: null,
  onError: null,
};

ImageCropModal.propTypes = {
  id: string.isRequired,
  isOpen: bool,
  src: string,
  fileName: string.isRequired,
  aspect: number.isRequired,
  aspectChangeable: bool,
  onAspectChange: func.isRequired,
  instructions: node,
  confirmLabel: node,
  onConfirm: func.isRequired,
  // Called with the error when the cropped image can't be encoded
  onError: func,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default ImageCropModal;
